import { useRef, useEffect, useState } from 'react';

// How long the last caption line lingers after the source text clears (ms).
const LINGER_MS = 2500;

export const CaptionBox = ({
  text,
  show,
  tone = 'assistant',
  variant = 'boxed',
  speaking = false,
}: {
  text: string;
  show: boolean;
  tone?: 'assistant' | 'user';
  /** 'bare' drops the border/background and renders flush against its parent — for embedding
   *  inside another bordered surface (VoiceComposer's combined card) instead of floating on its
   *  own. 'boxed' (default) keeps the standalone card look used elsewhere. */
  variant?: 'boxed' | 'bare';
  /** Shows an animated "SAM speaking" equalizer before the text (assistant tone only). */
  speaking?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const lingerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Displayed text: the live text when flowing, the last non-empty text during linger.
  const [displayed, setDisplayed] = useState(text);
  const [lingering, setLingering] = useState(false);

  useEffect(() => {
    if (text.trim()) {
      // Live text is flowing — cancel any pending linger timer.
      if (lingerTimer.current) clearTimeout(lingerTimer.current);
      setDisplayed(text);
      setLingering(false);
    } else if (displayed.trim()) {
      // Text just cleared but we have a previous value — linger briefly.
      setLingering(true);
      lingerTimer.current = setTimeout(() => {
        setLingering(false);
        setDisplayed('');
      }, LINGER_MS);
    }
    return () => { if (lingerTimer.current) clearTimeout(lingerTimer.current); };
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = show && !!(lingering ? displayed.trim() : text.trim());
  const bare = variant === 'bare';

  useEffect(() => {
    if (open && ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [displayed, open]);

  return (
    <div
      className={`flex-shrink-0 overflow-hidden px-4 transition-all duration-300 ease-in-out ${
        open ? `max-h-32 opacity-100 ${bare ? 'pt-3' : 'mb-2'}` : 'max-h-0 opacity-0 mb-0'
      }`}
      aria-hidden={!open}
    >
      <div
        ref={ref}
        className={`max-h-28 overflow-y-auto no-scrollbar transition-opacity duration-500 ${lingering ? 'opacity-50' : 'opacity-100'} ${
          bare ? '' : 'rounded-xl border border-ui-border px-4 py-3 bg-ui-subtle'
        }`}
      >
        <p className={`text-sm leading-snug text-ui-text flex items-start gap-2 ${tone === 'user' ? 'font-medium' : ''}`}>
          {tone === 'assistant' && (
            <span
              className={`voice-eq flex-shrink-0 mt-1 ${speaking ? '' : 'off'}`}
              aria-label={speaking ? 'SAM speaking' : undefined}
              aria-hidden={speaking ? undefined : true}
            >
              <i /><i /><i /><i />
            </span>
          )}
          <span>{lingering ? displayed : text}</span>
        </p>
      </div>
    </div>
  );
};

export default CaptionBox;
